import { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { FileCheck2, ScrollText, ShieldAlert, Plus, Download, Trash2, Search } from "lucide-react";

export default function AdminCompliance() {
  const [tab, setTab] = useState("cgv");
  return (
    <div className="space-y-6" data-testid="admin-compliance-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">Lot 3 — Conformité</p>
        <h1 className="text-3xl font-semibold text-white flex items-center gap-3"><ShieldAlert className="w-7 h-7 text-emerald-400" /> Conformité & RGPD</h1>
      </div>
      <div className="inline-flex bg-[#0f172a] border border-slate-800 rounded-md p-1">
        {[["cgv", "Registre CGV", ScrollText], ["gdpr", "RGPD (export & purge)", FileCheck2], ["corpus_diff", "Corpus (diff)", ShieldAlert]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`compliance-tab-${k}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs ${tab === k ? "bg-[#1F5A99] text-white" : "text-slate-400 hover:text-white"}`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>
      {tab === "cgv" && <CGV />}
      {tab === "gdpr" && <GDPR />}
      {tab === "corpus_diff" && <CorpusDiff />}
    </div>
  );
}

function CGV() {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ version: "", effective_at: "", content_html: "", notes: "", announce_days_before: 30 });
  const load = async () => { try { const {data} = await adminApi.get("/cgv"); setRows(data);} catch { toast.error("Erreur"); } };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (form.content_html.length < 100) return toast.error("Contenu min 100 chars");
    try { await adminApi.post("/cgv", form); toast.success("Version CGV créée"); setShowNew(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const activate = async (id) => {
    if (!window.confirm("Activer cette version ?")) return;
    try { await adminApi.post(`/cgv/${id}/activate`); toast.success("Activée"); load(); } catch { toast.error("Erreur"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <p className="text-xs text-slate-400">Chaque version horodatée avec SHA-256 + planification J-30.</p>
        <button onClick={() => setShowNew(!showNew)} data-testid="cgv-new-btn"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-600 text-white text-xs"><Plus className="w-3.5 h-3.5" /> Nouvelle version</button>
      </div>
      {showNew && (
        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5 space-y-3" data-testid="cgv-form">
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Version (ex 1.1)" value={form.version} onChange={e => setForm({...form, version: e.target.value})} data-testid="cgv-version" className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
            <input type="datetime-local" value={form.effective_at} onChange={e => setForm({...form, effective_at: e.target.value})} data-testid="cgv-effective" className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
          </div>
          <textarea rows={8} placeholder="Contenu HTML des CGV (min 100 caractères)" value={form.content_html} onChange={e => setForm({...form, content_html: e.target.value})} data-testid="cgv-content" className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs font-mono" />
          <input placeholder="Notes de version" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
          <button onClick={create} data-testid="cgv-create-btn" className="px-4 py-2 rounded-md bg-emerald-600 text-white text-xs">Publier</button>
        </div>
      )}
      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-4">
        <table className="w-full text-xs">
          <thead><tr className="uppercase text-[10px] text-slate-500 text-left"><th className="p-2">Version</th><th className="p-2">Effet</th><th className="p-2">SHA-256</th><th className="p-2">Acceptations</th><th className="p-2">Activée</th><th className="p-2"></th></tr></thead>
          <tbody>{rows.map(v => (
            <tr key={v.id} className="border-t border-slate-800" data-testid={`cgv-row-${v.version}`}>
              <td className="p-2 font-mono font-semibold text-white">v{v.version}</td>
              <td className="p-2 font-mono text-slate-500">{v.effective_at?.slice(0,10)}</td>
              <td className="p-2 font-mono text-slate-600">{v.sha256?.slice(0,16)}…</td>
              <td className="p-2 font-mono text-emerald-400">{v.acceptances_count}</td>
              <td className="p-2">{v.activated_at ? "✓" : "—"}</td>
              <td className="p-2">{!v.activated_at && <button onClick={() => activate(v.id)} className="text-emerald-400 hover:underline">Activer</button>}</td>
            </tr>
          ))}{rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">Aucune version</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}

function GDPR() {
  const [email, setEmail] = useState(""); const [target, setTarget] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState(""); const [keepInvoices, setKeepInvoices] = useState(true);
  const lookup = async () => {
    try { const {data} = await adminApi.get(`/gdpr/lookup?email=${encodeURIComponent(email)}`); setTarget(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Non trouvé"); setTarget(null); }
  };
  const dlExport = async () => {
    const token = localStorage.getItem("empco_admin_token");
    const r = await fetch(`${adminApi.defaults.baseURL}/gdpr/${target.user_id}/export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `empco-gdpr-${email}.zip`; a.click();
    toast.success("Export téléchargé");
  };
  const purge = async () => {
    if (confirmEmail !== target.email) return toast.error("E-mail incorrect");
    if (!window.confirm(`Purger définitivement ${target.email} ? ${keepInvoices ? "Les factures seront conservées 10 ans (anonymisées)." : "TOUT sera supprimé (contraire RGPD !)"}`)) return;
    try { await adminApi.post(`/gdpr/${target.user_id}/purge`, { confirm_email: confirmEmail, keep_invoices: keepInvoices });
      toast.success("Purge effectuée");
      setTarget(null); setConfirmEmail("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="space-y-4">
      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold mb-3">Recherche client (RGPD)</h3>
        <div className="flex gap-2">
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="e-mail@exemple.com" data-testid="gdpr-lookup-email"
            className="flex-1 bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
          <button onClick={lookup} data-testid="gdpr-lookup-btn" className="px-4 py-2 rounded-md bg-[#1F5A99] text-white text-xs"><Search className="w-3.5 h-3.5 inline" /> Chercher</button>
        </div>
      </div>
      {target && (
        <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5" data-testid="gdpr-target">
          <p className="text-xs text-slate-400 mb-3">Compte trouvé : <b className="text-white">{target.email}</b> (créé {target.created_at?.slice(0,10)})</p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4 text-center">
            {Object.entries(target.counts).map(([k, v]) => (
              <div key={k} className="p-2 bg-[#0b1220] rounded-md"><p className="text-[10px] text-slate-500 uppercase">{k}</p><p className="font-mono text-emerald-400">{v}</p></div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={dlExport} data-testid="gdpr-export-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-xs"><Download className="w-3.5 h-3.5" /> Export ZIP (JSON+PDFs)</button>
          </div>
          <div className="mt-5 border-t border-slate-800 pt-4">
            <p className="text-xs text-rose-400 mb-2">Zone dangereuse — Suppression anticipée (Article 17 RGPD)</p>
            <label className="text-xs flex items-center gap-2 mb-2">
              <input type="checkbox" checked={keepInvoices} onChange={e => setKeepInvoices(e.target.checked)} data-testid="gdpr-keep-invoices" />
              Conserver les factures 10 ans (anonymisées) — obligation légale
            </label>
            <div className="flex gap-2">
              <input value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} placeholder={`Retapez ${target.email}`} data-testid="gdpr-confirm-email"
                className="flex-1 bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs font-mono" />
              <button onClick={purge} disabled={confirmEmail !== target.email} data-testid="gdpr-purge-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-rose-600 disabled:opacity-40 text-white text-xs">
                <Trash2 className="w-3.5 h-3.5" /> Purger définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CorpusDiff() {
  const [versions, setVersions] = useState([]);
  const [a, setA] = useState(""); const [b, setB] = useState("");
  const [diff, setDiff] = useState(null);
  useEffect(() => { adminApi.get("/corpus").then(r => setVersions(r.data)); }, []);
  const compare = async () => {
    if (!a || !b) return toast.error("Choisir 2 versions");
    try { const {data} = await adminApi.get(`/corpus/${a}/diff/${b}`); setDiff(data);} catch { toast.error("Erreur"); }
  };
  return (
    <div className="space-y-4">
      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5">
        <div className="grid md:grid-cols-3 gap-3">
          <select value={a} onChange={e => setA(e.target.value)} data-testid="diff-a" className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs">
            <option value="">Version A</option>{versions.map(v => <option key={v.id} value={v.id}>v{v.version} {v.active ? "(active)" : ""}</option>)}
          </select>
          <select value={b} onChange={e => setB(e.target.value)} data-testid="diff-b" className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs">
            <option value="">Version B</option>{versions.map(v => <option key={v.id} value={v.id}>v{v.version} {v.active ? "(active)" : ""}</option>)}
          </select>
          <button onClick={compare} data-testid="diff-run" className="px-4 py-2 rounded-md bg-emerald-600 text-white text-xs">Comparer</button>
        </div>
      </div>
      {diff && (
        <div className="grid md:grid-cols-2 gap-4">
          {["fr", "en"].map(lang => (
            <div key={lang} className="bg-[#0f172a] border border-slate-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold mb-3">{lang.toUpperCase()}</h3>
              <div className="grid grid-cols-3 gap-2 mb-4 text-center text-xs">
                <div className="p-2 bg-emerald-500/10 rounded-md"><p className="text-slate-400">Ajoutés</p><p className="font-mono text-emerald-400 text-lg">{diff[lang].counts.added}</p></div>
                <div className="p-2 bg-rose-500/10 rounded-md"><p className="text-slate-400">Retirés</p><p className="font-mono text-rose-400 text-lg">{diff[lang].counts.removed}</p></div>
                <div className="p-2 bg-amber-500/10 rounded-md"><p className="text-slate-400">Modifiés</p><p className="font-mono text-amber-400 text-lg">{diff[lang].counts.modified}</p></div>
              </div>
              <div className="max-h-64 overflow-auto text-xs space-y-1">
                {diff[lang].added.slice(0, 20).map((t) => <p key={`add-${t.term}`} className="text-emerald-400">+ {t.term} <span className="text-slate-500">({t.severity}/{t.category})</span></p>)}
                {diff[lang].removed.slice(0, 20).map((t) => <p key={`rem-${t.term}`} className="text-rose-400">- {t.term} <span className="text-slate-500">({t.severity}/{t.category})</span></p>)}
                {diff[lang].modified.slice(0, 20).map((t) => <p key={`mod-${t.term}`} className="text-amber-400">~ {t.term}</p>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
